/**
 * Supermarket POS  (Screen ID: 6.16.12)
 * ─────────────────────────────────────────────────────────────────────────
 * Fast checkout — direct item add (no variant picker), barcode scan,
 * weight-based items, quick numpad for qty entry.
 * Same CSS design language as ApparelPOS (--surface, --navy, --blue, etc.)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import './SupermarketPOS.css';

const SM_CATEGORIES = {
  'Fruits & Veg': [
    {code:'FV001',nm:'Banana (Dozen)',    unit:'dz', rate:55,   barcode:'8901011234', icon:'🍌', taxPct:0},
    {code:'FV002',nm:'Apple (1 kg)',       unit:'kg', rate:180,  barcode:'8901021234', icon:'🍎', taxPct:0},
    {code:'FV003',nm:'Tomato (1 kg)',      unit:'kg', rate:40,   barcode:'8901031234', icon:'🍅', taxPct:0},
    {code:'FV004',nm:'Onion (1 kg)',       unit:'kg', rate:35,   barcode:'8901041234', icon:'🧅', taxPct:0},
    {code:'FV005',nm:'Potato (1 kg)',      unit:'kg', rate:30,   barcode:'8901051234', icon:'🥔', taxPct:0},
  ],
  'Dairy & Bakery': [
    {code:'DB001',nm:'Amul Milk 500ml',    unit:'pcs',rate:28,   barcode:'8902011234', icon:'🥛', taxPct:5},
    {code:'DB002',nm:'Amul Butter 100g',   unit:'pcs',rate:55,   barcode:'8902021234', icon:'🧈', taxPct:5},
    {code:'DB003',nm:'Bread (Loaf)',        unit:'pcs',rate:40,   barcode:'8902031234', icon:'🍞', taxPct:5},
    {code:'DB004',nm:'Curd 400g',           unit:'pcs',rate:35,   barcode:'8902041234', icon:'🥛', taxPct:5},
    {code:'DB005',nm:'Paneer 200g',         unit:'pcs',rate:75,   barcode:'8902051234', icon:'🧀', taxPct:5},
  ],
  'Staples': [
    {code:'ST001',nm:'Rice (1 kg)',         unit:'kg', rate:55,   barcode:'8903011234', icon:'🍚', taxPct:0},
    {code:'ST002',nm:'Atta (1 kg)',          unit:'kg', rate:42,   barcode:'8903021234', icon:'🌾', taxPct:0},
    {code:'ST003',nm:'Sugar (1 kg)',         unit:'kg', rate:45,   barcode:'8903031234', icon:'🍬', taxPct:5},
    {code:'ST004',nm:'Salt (1 kg)',          unit:'kg', rate:20,   barcode:'8903041234', icon:'🧂', taxPct:0},
    {code:'ST005',nm:'Toor Dal (1 kg)',      unit:'kg', rate:130,  barcode:'8903051234', icon:'🫘', taxPct:0},
  ],
  'Snacks': [
    {code:'SN001',nm:'Lays Classic 26g',    unit:'pcs',rate:20,   barcode:'8904011234', icon:'🥔', taxPct:12},
    {code:'SN002',nm:'Parle-G Biscuit',      unit:'pcs',rate:10,   barcode:'8904021234', icon:'🍪', taxPct:12},
    {code:'SN003',nm:'Kurkure 26g',          unit:'pcs',rate:20,   barcode:'8904031234', icon:'🌽', taxPct:12},
    {code:'SN004',nm:'Good Day 100g',        unit:'pcs',rate:30,   barcode:'8904041234', icon:'🍪', taxPct:12},
  ],
  'Beverages': [
    {code:'BV001',nm:'Coca-Cola 750ml',      unit:'pcs',rate:40,   barcode:'8905011234', icon:'🥤', taxPct:18},
    {code:'BV002',nm:'Tata Tea 100g',         unit:'pcs',rate:50,   barcode:'8905021234', icon:'🍵', taxPct:5},
    {code:'BV003',nm:'Nescafe 50g',           unit:'pcs',rate:155,  barcode:'8905031234', icon:'☕', taxPct:5},
    {code:'BV004',nm:'Minute Maid 1L',        unit:'pcs',rate:80,   barcode:'8905041234', icon:'🧃', taxPct:12},
  ],
  'Personal Care': [
    {code:'PC001',nm:'Dove Soap 75g',        unit:'pcs',rate:42,   barcode:'8906011234', icon:'🧼', taxPct:18},
    {code:'PC002',nm:'Colgate 150g',          unit:'pcs',rate:60,   barcode:'8906021234', icon:'🦷', taxPct:18},
    {code:'PC003',nm:'Dettol 750ml',          unit:'pcs',rate:175,  barcode:'8906031234', icon:'🧴', taxPct:18},
  ],
};
const ALL_SM = Object.values(SM_CATEGORIES).flat();
const SM_CATS = ['All', ...Object.keys(SM_CATEGORIES)];

const PAY_MODES = [
  {id:'cash',  ic:'₹',  lb:'Cash'},
  {id:'upi',   ic:'📱', lb:'UPI'},
  {id:'card',  ic:'💳', lb:'Card'},
  {id:'credit',ic:'📋', lb:'Credit'},
];

function fmt(n) { return (isNaN(n)?0:n).toLocaleString('en-IN',{minimumFractionDigits:2,maximumFractionDigits:2}); }
function nowStr() { return new Date().toLocaleTimeString('en-IN',{hour12:false}); }
function todayStr() { return new Date().toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}); }

export default function SupermarketPOSScreen({ theme, navigateTo }) {
  const [cat,      setCat]      = useState('All');
  const [searchQ,  setSearchQ]  = useState('');
  const [order,    setOrder]    = useState([]);
  const [payMode,  setPayMode]  = useState('cash');
  const [tendered, setTendered] = useState('');
  const [clock,    setClock]    = useState(nowStr());
  const [toast,    setToast]    = useState('');
  const [toastOn,  setToastOn]  = useState(false);
  const barcodeRef = useRef(null);
  const toastRef   = useRef(null);

  useEffect(() => { const t = setInterval(() => setClock(nowStr()), 1000); return () => clearInterval(t); }, []);

  const showToast = useCallback((msg) => {
    setToast(msg); setToastOn(true);
    clearTimeout(toastRef.current);
    toastRef.current = setTimeout(() => setToastOn(false), 1700);
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (e.key === 'F10') { e.preventDefault(); handleSave(); }
      if (e.key === 'F2')  { e.preventDefault(); voidLast(); }
      if (e.key === 'Escape' && barcodeRef.current) barcodeRef.current.focus();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // Barcode scan / search
  const tiles = (() => {
    const pool = cat === 'All' ? ALL_SM : (SM_CATEGORIES[cat] || []);
    if (!searchQ.trim()) return pool;
    const q = searchQ.toLowerCase();
    return ALL_SM.filter(it => it.nm.toLowerCase().includes(q) || it.code.toLowerCase().includes(q) || it.barcode.includes(q));
  })();

  function addItem(item, qty = 1) {
    setOrder(prev => {
      const idx = prev.findIndex(r => r.code === item.code);
      if (idx >= 0) { const u=[...prev]; u[idx]={...u[idx],qty:u[idx].qty+qty}; return u; }
      return [...prev, { ...item, qty }];
    });
    showToast(`${item.nm} added`);
    setSearchQ('');
    if (barcodeRef.current) barcodeRef.current.focus();
  }

  function handleBarcodeEnter(e) {
    if (e.key !== 'Enter') return;
    const q = searchQ.trim();
    const exact = ALL_SM.find(it => it.barcode === q || it.code === q);
    if (exact) addItem(exact);
    else if (tiles.length === 1) addItem(tiles[0]);
    else showToast('No exact match — press item tile');
  }

  function voidLast() {
    if (!order.length) return;
    setOrder(prev => prev.slice(0, -1));
    showToast('Last item voided (F2)');
  }

  function adjustQty(idx, delta) {
    setOrder(prev => {
      const u = [...prev];
      const newQty = u[idx].qty + delta;
      if (newQty <= 0) return u.filter((_, i) => i !== idx);
      u[idx] = { ...u[idx], qty: newQty };
      return u;
    });
  }

  const totQty   = order.reduce((s,r) => s + r.qty, 0);
  const subTotal = order.reduce((s,r) => s + r.rate * r.qty, 0);
  const taxAmt   = order.reduce((s,r) => s + r.rate * r.qty * r.taxPct / 100, 0);
  const grandTotal = Math.round(subTotal + taxAmt);
  const change     = Math.max(0, (+tendered||0) - grandTotal);

  function handleSave() {
    if (!order.length) { showToast('Add items before saving'); return; }
    showToast('Bill saved! Total ₹' + fmt(grandTotal));
  }

  function handleClear() {
    setOrder([]); setTendered(''); showToast('Bill cleared');
  }

  function confirmPayment() {
    if (+tendered < grandTotal) { showToast('Tendered amount is less than total'); return; }
    showToast('Payment confirmed! Change ₹' + fmt(change));
    setTimeout(handleClear, 1500);
  }

  return (
    <div className="smpos-root" data-testid="supermarket-pos-screen">
      <div className="smpos-main">

        {/* ── ORDER PANEL ─────────────────────────────────────────────── */}
        <div className="smpos-order">
          <div className="smpos-order-head">
            <span>Item</span><span>Qty</span><span>Rate</span><span>Amount</span>
          </div>
          <div className="smpos-order-list">
            {order.length === 0
              ? <div className="smpos-empty">Scan barcode or select item</div>
              : order.map((r, i) => (
                <div className="smpos-order-row" key={i}>
                  <div className="smpos-row-nm">
                    {r.nm}
                    <span className="smpos-row-unit">{r.unit}</span>
                  </div>
                  <div className="smpos-qty-ctrl">
                    <button onClick={() => adjustQty(i, -1)}>−</button>
                    <span className="smpos-mono">{r.qty}</span>
                    <button onClick={() => adjustQty(i, +1)}>+</button>
                  </div>
                  <span className="smpos-row-rate smpos-mono">₹{r.rate}</span>
                  <span className="smpos-row-val smpos-mono">₹{fmt(r.rate * r.qty)}</span>
                </div>
              ))
            }
          </div>
          <div className="smpos-order-total-line">
            <span className="smpos-mono">{totQty} items</span>
            <span className="smpos-mono">₹{fmt(subTotal + taxAmt)}</span>
          </div>

          <div className="smpos-order-actions">
            <div className="smpos-oa" onClick={() => showToast('Old bill…')}><div>📌</div><div>Old Bill</div></div>
            <div className="smpos-oa" onClick={voidLast}><div>🗑</div><div>Void (F2)</div></div>
            <div className="smpos-oa" onClick={() => showToast('Hold…')}><div>📁</div><div>Hold</div></div>
            <div className="smpos-oa" onClick={() => showToast('Get hold…')}><div>🗂</div><div>Get Hold</div></div>
          </div>

          <div className="smpos-info-grid">
            <div className="smpos-info"><label>Customer</label><span>Walk-in</span></div>
            <div className="smpos-info"><label>Total Qty</label><span className="smpos-mono">{totQty}</span></div>
            <div className="smpos-info"><label>Subtotal</label><span className="smpos-mono">₹{fmt(subTotal)}</span></div>
            <div className="smpos-info"><label>GST</label><span className="smpos-mono">₹{fmt(taxAmt)}</span></div>
            <div className="smpos-info"><label>Round Off</label><span className="smpos-mono">{((grandTotal - subTotal - taxAmt)).toFixed(2)}</span></div>
            <div className="smpos-info"><label>Items</label><span className="smpos-mono">{order.length} types</span></div>
          </div>

          <div className="smpos-total-bar">
            <span>Total Amount</span>
            <span><span className="smpos-cur">INR</span><span className="smpos-mono" data-testid="sm-grand-total">₹{fmt(grandTotal)}</span></span>
          </div>
        </div>

        {/* ── WORKSPACE ───────────────────────────────────────────────── */}
        <div className="smpos-workspace">
          {/* Search / Barcode bar */}
          <div className="smpos-scan-bar">
            <span className="smpos-scan-ic">📷</span>
            <input ref={barcodeRef}
              placeholder="Scan barcode or type item code / name — press Enter to add"
              value={searchQ}
              onChange={e => setSearchQ(e.target.value)}
              onKeyDown={handleBarcodeEnter}
              autoFocus
              data-testid="sm-barcode-input"
            />
            {searchQ && <button className="smpos-scan-clear" onClick={() => setSearchQ('')}>✕</button>}
          </div>

          {/* Category tabs */}
          <div className="smpos-cat-bar">
            {SM_CATS.map(c => (
              <button key={c} className={cat===c?'active':''} onClick={() => { setCat(c); setSearchQ(''); }} data-testid={`sm-cat-${c}`}>
                {c}
              </button>
            ))}
          </div>

          {/* Item grid */}
          <div className="smpos-tile-grid" data-testid="sm-tile-grid">
            {tiles.map(it => (
              <div key={it.code} className="smpos-tile" onClick={() => addItem(it)} data-testid={`sm-tile-${it.code}`}>
                <div className="smpos-tile-sw">{it.icon}</div>
                <div className="smpos-tile-code">{it.code}</div>
                <div className="smpos-tile-nm">{it.nm}</div>
                <div className="smpos-tile-footer">
                  <span className="smpos-tile-unit">{it.unit}</span>
                  <span className="smpos-tile-rate smpos-mono">₹{it.rate}</span>
                  {it.taxPct > 0 && <span className="smpos-tile-gst">GST {it.taxPct}%</span>}
                </div>
              </div>
            ))}
            {tiles.length === 0 && <div className="smpos-tile-empty">No items match "{searchQ}"</div>}
          </div>

          {/* Payment strip */}
          <div className="smpos-pay-strip">
            <div className="smpos-pay-due">
              <span>Amount Due</span>
              <span className="smpos-mono" data-testid="sm-pay-due">₹{fmt(grandTotal)}</span>
            </div>
            <div className="smpos-pay-modes">
              {PAY_MODES.map(m => (
                <div key={m.id} className={`smpos-pm${payMode===m.id?' active':''}`} onClick={() => setPayMode(m.id)} data-testid={`sm-pay-${m.id}`}>
                  <span>{m.ic}</span><span>{m.lb}</span>
                </div>
              ))}
            </div>
            <div className="smpos-tender-row">
              <div className="smpos-tender-box">
                <label>Tendered</label>
                <input type="number" className="smpos-mono" placeholder="0.00" value={tendered} onChange={e => setTendered(e.target.value)} data-testid="sm-tendered" />
              </div>
              <div className="smpos-tender-box">
                <label>Change</label>
                <input className="smpos-mono" value={fmt(change)} readOnly data-testid="sm-change" />
              </div>
              <div className="smpos-quick-amts">
                {[100,200,500,1000].map(a => <button key={a} onClick={() => setTendered(String(a))}>₹{a}</button>)}
                <button className="exact" onClick={() => setTendered(String(grandTotal))}>Exact</button>
              </div>
            </div>
            <button className="smpos-confirm" onClick={confirmPayment} data-testid="sm-confirm-pay">✓ Confirm Payment — ₹{fmt(grandTotal)}</button>
          </div>
        </div>

        {/* ── FUNCTION RAIL ────────────────────────────────────────────── */}
        <div className="smpos-rail">
          {[{ic:'⋮',lb:'More'},{ic:'🖨',lb:'Print Bill'},{ic:'📌',lb:'Old Bill'},{ic:'🔄',lb:'Exchange'},{ic:'↩',lb:'Return'}].map(b => (
            <div key={b.lb} className="smpos-fr-btn" onClick={() => showToast(b.lb)}>
              <div className="smpos-fr-ic">{b.ic}</div><div className="smpos-fr-lb">{b.lb}</div>
            </div>
          ))}
          <div className="smpos-fr-btn save" onClick={handleSave} data-testid="sm-save"><div className="smpos-fr-ic">💾</div><div className="smpos-fr-lb">Save (F10)</div></div>
          <div className="smpos-fr-btn" onClick={handleClear}><div className="smpos-fr-ic">🔄</div><div className="smpos-fr-lb">Clear</div></div>
          <div className="smpos-fr-btn exit" onClick={() => navigateTo && navigateTo('HUB')}><div className="smpos-fr-ic">⏻</div><div className="smpos-fr-lb">Exit</div></div>
        </div>
      </div>

      <div className="smpos-status">
        <span>Organisation: <b>RetailCLIQ ERP</b></span>
        <span>ScreenId <b>6.16.12</b></span>
        <span>Caption: <b>Supermarket POS</b></span>
        <span>User: <b>SSADMIN</b> <span className="smpos-mono">{clock}</span></span>
        <span>Business Day: <b className="smpos-mono">{todayStr()}</b></span>
        <span>Fin. Year: <b>2025–2026</b></span>
        <div className="smpos-sb-sp">
          <div className="smpos-sb-ic">?</div><div className="smpos-sb-ic">💬</div><div className="smpos-sb-ic">📅</div>
        </div>
      </div>

      <div className={`smpos-toast${toastOn?' show':''}`}>{toast}</div>
    </div>
  );
}
